import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Loader2, FileText, Send, Save } from "lucide-react";

/**
 * Modal for completing a draft business application from the admin Applications tab.
 * Sends PUT /api/admin/business-applications/{id} as multipart form-data.
 * Set status="pending" to promote a draft, or leave as draft to keep editing later.
 */
const EditApplicationModal = ({ application, subLocations = [], onClose, onSaved, getAuthHeader, apiBase }) => {
  const [form, setForm] = useState({
    name: application.name || "",
    email: application.email || "",
    phone: application.phone || "",
    business_name: application.business_name || "",
    description: application.description || "",
    address: application.address || "",
    category: application.category || "dining",
    discount_level: String(application.discount_level ?? "15"),
    website: application.website || "",
    tour_3d_url: application.tour_3d_url || "",
    sub_location: application.sub_location || "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [agreementFile, setAgreementFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const buildFormData = (status) => {
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== "") fd.append(k, v);
    });
    if (status) fd.append("status", status);
    if (imageFile) fd.append("image", imageFile);
    if (agreementFile) fd.append("signed_agreement", agreementFile);
    return fd;
  };

  const submit = async (status) => {
    setSaving(true);
    try {
      const axios = (await import("axios")).default;
      await axios.put(`${apiBase}/admin/business-applications/${application.id}`, buildFormData(status), {
        ...getAuthHeader(),
        headers: { ...(getAuthHeader().headers || {}), "Content-Type": "multipart/form-data" },
      });
      onSaved?.(status);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to save application";
      alert(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0B0F19] border-[#D4AF37]/20 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Playfair_Display'] text-xl flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#D4AF37]" />
            {application.status === "draft" ? "Complete draft application" : "Edit application"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-white/60 text-xs">Contact name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-white/60 text-xs">Contact email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-white/60 text-xs">Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-white/60 text-xs">Business name</Label>
              <Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="bg-white/5 border-white/10 text-white" />
            </div>
          </div>

          <div>
            <Label className="text-white/60 text-xs">Description</Label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-white text-sm focus:border-[#D4AF37] focus:outline-none"
            />
          </div>

          <div>
            <Label className="text-white/60 text-xs">Address</Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-white/5 border-white/10 text-white" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-white/60 text-xs">Sub-location</Label>
              <Select value={form.sub_location} onValueChange={(v) => setForm({ ...form, sub_location: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {subLocations.map((loc) => <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/60 text-xs">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dining">Dining & Restaurants</SelectItem>
                  <SelectItem value="shopping">Shopping & Retail</SelectItem>
                  <SelectItem value="services">Services & Experiences</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/60 text-xs">Discount %</Label>
              <Select value={form.discount_level} onValueChange={(v) => setForm({ ...form, discount_level: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10%</SelectItem>
                  <SelectItem value="15">15%</SelectItem>
                  <SelectItem value="20">20%</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-white/60 text-xs">Website</Label>
              <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-white/60 text-xs">3D tour URL</Label>
              <Input value={form.tour_3d_url} onChange={(e) => setForm({ ...form, tour_3d_url: e.target.value })} className="bg-white/5 border-white/10 text-white" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-white/60 text-xs">Business photo (optional)</Label>
              <Input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files?.[0] || null)} className="bg-white/5 border-white/10 text-white text-xs" />
            </div>
            <div>
              <Label className="text-white/60 text-xs">
                Signed agreement PDF {application.signed_agreement_url ? "(replace existing)" : "(upload to attach)"}
              </Label>
              <Input type="file" accept="application/pdf" onChange={(e) => setAgreementFile(e.target.files?.[0] || null)} className="bg-white/5 border-white/10 text-white text-xs" />
            </div>
          </div>

          {application.signed_agreement_url && (
            <p className="text-[#D4AF37]/80 text-xs flex items-center gap-1">
              <FileText className="w-3 h-3" />
              Existing signed agreement on file — will be kept unless you upload a new one.
            </p>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2 mt-5 pt-4 border-t border-white/10">
          <Button variant="ghost" onClick={onClose} disabled={saving} className="text-white/70 hover:text-white">Cancel</Button>
          <Button
            variant="outline"
            onClick={() => submit("draft")}
            disabled={saving}
            className="border-white/20 text-white/80 hover:text-white"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save as Draft
          </Button>
          <Button
            onClick={() => submit("pending")}
            disabled={saving}
            className="btn-gold"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Submit for Review
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditApplicationModal;
